import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Compass, Lightning, UserCircle, SignOut, ChatCircleDots, CalendarBlank, CaretDown } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import FeedbackPrompt from "./FeedbackPrompt";

const NAV = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/walkthrough?mode=guided", label: "Guide Me Through a Difficult Conversation", testid: "nav-walkthrough", match: "/walkthrough" },
  { to: "/coach", label: "Work with a Coach", testid: "nav-coach", dropdown: [
    { to: "/coach", label: "Work With a Coach Now", testid: "nav-coach-new" },
    { to: "/coach/my", label: "Contact Your Coach Now", testid: "nav-coach-mine" },
  ] },
];

function UserMenu() {
  const { coach, leader, signOutCoach, signOutLeader } = useAuth();
  const [open, setOpen] = useState(false);

  // Coach console takes priority — they signed in on this device
  if (coach) {
    return (
      <div className="relative">
        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="header-user-menu"
          className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm bg-[#F3F0EA] hover:bg-[#E8E4DB] text-[#1E3A2B]"
        >
          <UserCircle size={16} weight="duotone" />
          {coach.name?.split(" ")[0] || "Coach"}
          <CaretDown size={12} weight="bold" />
        </button>
        {open && (
          <div
            className="absolute right-0 mt-2 w-56 bg-white border border-[#E8E4DB] rounded-xl shadow-lg overflow-hidden z-50"
            onMouseLeave={() => setOpen(false)}
          >
            <Link to="/coach/dashboard" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[#F3F0EA] text-[#121815]" data-testid="menu-coach-dashboard">
              <ChatCircleDots size={14} weight="duotone" /> Dashboard
            </Link>
            <Link to="/coach/availability" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[#F3F0EA] text-[#121815]" data-testid="menu-coach-availability">
              <CalendarBlank size={14} weight="duotone" /> Availability
            </Link>
            <button
              onClick={() => { signOutCoach(); setOpen(false); }}
              className="w-full text-left flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[#F3F0EA] text-[#C77D5E] border-t border-[#E8E4DB]"
              data-testid="menu-coach-signout"
            >
              <SignOut size={14} weight="duotone" /> Sign out
            </button>
          </div>
        )}
      </div>
    );
  }
  if (leader) {
    return (
      <div className="relative">
        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="header-user-menu"
          className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm bg-[#F3F0EA] hover:bg-[#E8E4DB] text-[#1E3A2B]"
        >
          <UserCircle size={16} weight="duotone" />
          {leader.name?.split(" ")[0] || leader.email.split("@")[0]}
          <CaretDown size={12} weight="bold" />
        </button>
        {open && (
          <div
            className="absolute right-0 mt-2 w-56 bg-white border border-[#E8E4DB] rounded-xl shadow-lg overflow-hidden z-50"
            onMouseLeave={() => setOpen(false)}
          >
            <Link to="/my/conversations" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[#F3F0EA] text-[#121815]" data-testid="menu-leader-conversations">
              <ChatCircleDots size={14} weight="duotone" /> My conversations
            </Link>
            <button
              onClick={() => { signOutLeader(); setOpen(false); }}
              className="w-full text-left flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[#F3F0EA] text-[#C77D5E] border-t border-[#E8E4DB]"
              data-testid="menu-leader-signout"
            >
              <SignOut size={14} weight="duotone" /> Sign out
            </button>
          </div>
        )}
      </div>
    );
  }
  return (
    <Link
      to="/auth/login"
      data-testid="header-signin-link"
      className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm text-[#4A524C] hover:text-[#1E3A2B] hover:bg-[#F3F0EA]"
    >
      Sign in
    </Link>
  );
}

export default function Layout({ children }) {
  const loc = useLocation();
  const { coach } = useAuth();
  // Coaches don't need the leader-facing "Walkthrough" and "Contact My Coach"
  // navigation — those are tools for leaders in active conflicts.
  const nav = coach ? [{ to: "/coach/dashboard", label: "Dashboard", testid: "nav-coach-dashboard" }] : NAV;
  const isNavActive = (n) => {
    if (n.to === "/") return loc.pathname === "/";
    if (!n.to.includes("?")) return loc.pathname === n.to || loc.pathname.startsWith(n.to + "/");
    // For deep-links like /walkthrough?mode=quick, require both pathname AND query to match
    const [path, qs] = n.to.split("?");
    if (loc.pathname !== path) return false;
    const params = new URLSearchParams(qs);
    const cur = new URLSearchParams(loc.search);
    for (const [k, v] of params.entries()) {
      if (cur.get(k) !== v) return false;
    }
    return true;
  };
  return (
    <div className="min-h-screen flex flex-col bg-[#FDFBF7] text-[#121815]">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <a
              href="https://www.commongroundinstitute.net"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="brand-cgi-link"
              title="Common Ground Institute"
              className="flex-shrink-0"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_executive-mediate/artifacts/9cjzp6eb_CGI_Logo_Final_051926_bluegold.png"
                alt="Common Ground Institute"
                className="h-10 w-auto rounded-md"
                data-testid="brand-logo"
              />
            </a>
            <span className="hidden xl:inline-block w-px h-7 bg-[#E8E4DB]" aria-hidden="true" />
            <Link
              to="/"
              data-testid="brand-home-link"
              className="hidden xl:inline font-serif text-lg text-[#1E3A2B] tracking-tight whitespace-nowrap hover:text-[#2C4C3B]"
            >
              Conflict Coach for Executives
            </Link>
          </div>
          <nav className="hidden md:flex items-center gap-1">
            {nav.map((n) => {
              const active = isNavActive(n);
              if (n.dropdown) {
                return (
                  <div key={n.to} className="relative group">
                    <button
                      data-testid={n.testid}
                      className={`px-4 py-2 rounded-full text-sm transition-all inline-flex items-center gap-1 ${
                        active ? "bg-[#1E3A2B] text-white" : "text-[#4A524C] hover:text-[#1E3A2B] hover:bg-[#F3F0EA]"
                      }`}
                    >
                      {n.label}
                      <span aria-hidden="true" className="text-xs">▾</span>
                    </button>
                    <div className="absolute right-0 top-full pt-2 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto transition-opacity z-40">
                      <div className="bg-white border border-[#E8E4DB] rounded-2xl shadow-lg py-1.5 min-w-[220px]">
                        {n.dropdown.map((d) => (
                          <Link
                            key={d.testid}
                            to={d.to}
                            data-testid={d.testid}
                            className="block px-4 py-2 text-sm text-[#1E3A2B] hover:bg-[#F3F0EA]"
                          >
                            {d.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={n.testid}
                  className={`px-4 py-2 rounded-full text-sm transition-all ${
                    active ? "bg-[#1E3A2B] text-white" : "text-[#4A524C] hover:text-[#1E3A2B] hover:bg-[#F3F0EA]"
                  }`}
                >
                  {n.label}
                </Link>
              );
            })}
          </nav>
          <div className="flex items-center gap-2">
            <UserMenu />
          </div>
        </div>
        {/* mobile nav */}
        <nav className="md:hidden border-t border-[#E8E4DB] overflow-x-auto">
          <div className="flex px-4 py-2 gap-1">
            {nav.flatMap((n) => {
              if (n.dropdown) {
                return n.dropdown.map((d) => ({ ...d, mobile: true }));
              }
              return [n];
            }).map((n) => {
              const active = isNavActive(n);
              return (
                <Link
                  key={n.testid}
                  to={n.to}
                  data-testid={`${n.testid}-mobile`}
                  className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${
                    active ? "bg-[#1E3A2B] text-white" : "text-[#4A524C]"
                  }`}
                >
                  {n.label}
                </Link>
              );
            })}
          </div>
        </nav>
      </header>

      <main className="flex-1" key={loc.pathname}>
        {children}
      </main>

      {/* Site-wide feedback CTA — appears above the footer on every page.
          Prominent by design; the app is in early access and every note counts. */}
      <section className="border-t border-[#E8E4DB] bg-[#FDFBF7]">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 py-10">
          <FeedbackPrompt variant="banner" />
        </div>
      </section>

      <footer className="border-t border-[#E8E4DB]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10 grid md:grid-cols-2 gap-8 text-sm text-[#4A524C]">
          <div data-testid="footer-did-you-know">
            <div className="label-kicker mb-3">Did You Know:</div>
            <ul className="space-y-2.5 text-[#4A524C] leading-relaxed">
              <li>That 49% of managers cannot effectively manage conflict?</li>
              <li>Employees lose 2.8 hours per week to conflict?</li>
              <li>That 62% of professionals have considered quitting because of workplace conflict?</li>
            </ul>
          </div>
          <div>
            <div className="label-kicker mb-3">Important</div>
            <p className="text-[#8A948E] leading-relaxed text-xs">
              This service is for executive guidance only and is not a
              substitute for professional legal, HR, financial, medical, or
              psychological advice or therapy. Your conversations are private
              and are not accessible to the broader internet. To power AI
              responses, your messages are processed by Anthropic and OpenAI
              under their API data policies, which do not use customer data
              for model training.
            </p>
          </div>
        </div>
        <div className="border-t border-[#E8E4DB] py-4 text-center text-xs text-[#8A948E] max-w-7xl mx-auto px-6 sm:px-10">
          © {new Date().getFullYear()} Conflict Coach for Executives
        </div>
      </footer>
    </div>
  );
}
